import { useState, useEffect, useRef } from "react";
import { toPng } from "html-to-image";
import { Sparkles, Heart, Coins, Users, HandHeart, Gift, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { LOGO } from "../lib/constants";
import api, { fileUrl } from "../lib/api";
import { toast } from "sonner";

function Stat({ icon: Icon, value, label, accent = "copper" }) {
  const colors = { copper: "text-copper", penny: "text-penny", sage: "text-sage" };
  return (
    <div className="bg-secondary rounded-2xl p-4 text-center">
      <Icon className={`w-6 h-6 mx-auto ${colors[accent] || colors.copper}`} />
      <p className="font-serif text-3xl font-bold text-bronze mt-1 tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

export default function RecapModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [sharing, setSharing] = useState(false);
  const cardRef = useRef();

  useEffect(() => {
    if (open) { setData(null); api.get("/recap").then((r) => setData(r.data)).catch(() => {}); }
  }, [open]);

  const shareRecap = async () => {
    if (!cardRef.current) return;
    setSharing(true);
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2, skipFonts: true });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "haplap-recap.png", { type: "image/png" });
      const shareText = "This week on HAPLAP — a community helping real people directly, one penny at a time. 💛 Come support someone today!";
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My week on HAPLAP", text: shareText });
      } else {
        const a = document.createElement("a"); a.href = dataUrl; a.download = "haplap-recap.png"; a.click();
        window.open("https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(window.location.origin), "_blank", "noopener");
        toast.success("Saved your recap card! Post it to Instagram or Facebook to draw in supporters 💛");
      }
    } catch (e) {
      toast.error("Couldn't create your card. Please try again.");
    }
    setSharing(false);
  };

  const r = data?.received;
  const g = data?.giving;
  const gotSomething = r && r.total > 0;
  const gaveSomething = g && g.total > 0;
  const firstName = data?.name?.split(" ")[0] || "friend";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="recap-modal" className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto p-0">
        <DialogTitle className="sr-only">Your Weekly Recap</DialogTitle>
        <div className="theme-copper px-6 pt-8 pb-6 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20 paper-bg" />
          <img src={LOGO} alt="" className="w-14 h-14 rounded-2xl mx-auto shadow-lg relative" />
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/90 mt-3 relative">Your Week on HAPLAP</p>
          <h2 className="font-serif text-3xl font-bold text-white mt-1 relative">Hey {firstName} 💛</h2>
          <p className="text-white/90 text-sm mt-1 relative">Here's the good you were part of this week.</p>
        </div>

        {!data ? (
          <div className="p-10 text-center text-copper font-serif">Gathering your good vibes…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Received */}
            {gotSomething || r.new_supporters > 0 ? (
              <div>
                <h3 className="font-serif text-xl font-semibold text-bronze mb-3 flex items-center gap-2"><Heart className="w-5 h-5 text-copper fill-copper" /> You were lifted up</h3>
                <div className="grid grid-cols-3 gap-3">
                  <Stat icon={Users} value={r.new_supporters} label="new supporters" />
                  <Stat icon={Coins} value={`$${r.tips_total.toFixed(0)}`} label="in direct tips" accent="penny" />
                  <Stat icon={Gift} value={`$${r.jar_total.toFixed(0)}`} label="from the jar" accent="sage" />
                </div>
                {r.supporter_people?.length > 0 && (
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <span className="text-sm text-muted-foreground">New kindness from</span>
                    {r.supporter_people.slice(0, 6).map((p) => (
                      <img key={p.user_id} src={fileUrl(p.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${p.user_id}`}
                        alt={p.name} title={p.name} className="w-7 h-7 rounded-full bg-secondary" />
                    ))}
                  </div>
                )}
                <p className="text-sm text-foreground/80 mt-3 leading-relaxed bg-copper/10 rounded-2xl p-3">
                  {gotSomething
                    ? `That's $${r.total.toFixed(2)} of real, direct human kindness — from people who chose you. You are seen, ${firstName}, and you matter. 🫶`
                    : `New people are cheering you on. Keep sharing your story — your community is growing. 🌱`}
                </p>
              </div>
            ) : null}

            {/* Giving */}
            {gaveSomething && (
              <div>
                <h3 className="font-serif text-xl font-semibold text-bronze mb-3 flex items-center gap-2"><HandHeart className="w-5 h-5 text-sage" /> You changed lives</h3>
                <div className="grid grid-cols-3 gap-3">
                  <Stat icon={Coins} value={`$${g.total.toFixed(0)}`} label="given this week" accent="penny" />
                  <Stat icon={Users} value={g.people_helped} label="people helped directly" />
                  <Stat icon={Sparkles} value={g.jar_count > 0 ? g.jar_reaches : 0} label="reached via the jar" accent="sage" />
                </div>
                <p className="text-sm text-foreground/80 mt-3 leading-relaxed bg-sage/10 rounded-2xl p-3">
                  Every dollar you gave went <b>straight into real people's hands</b> — not a giant organization, not CEO salaries, not overhead.
                  {g.jar_count > 0 && ` Your community jar gift alone touched all ${g.jar_reaches} members seeking help.`} That's rare. That's you quietly changing the world. ✨
                </p>
              </div>
            )}

            {/* Universal warm close */}
            <div className="text-center bg-gradient-to-br from-penny/10 to-copper/10 rounded-2xl p-5">
              <Sparkles className="w-6 h-6 text-penny mx-auto" />
              <p className="font-serif text-lg text-bronze mt-2 leading-snug">
                {gaveSomething
                  ? "Good karma has a way of finding people like you."
                  : "Your courage to be here is its own kind of brave."}
              </p>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                HAPLAP exists for one reason: <b>people helping people, directly</b>. No middlemen. No shame. Just a community
                that shows up for each other, one penny at a time. Thank you for being part of it, {firstName}.
              </p>
            </div>

            <div className="flex gap-2">
              <Button data-testid="recap-share" onClick={shareRecap} disabled={sharing}
                className="flex-1 bg-sage hover:bg-sage/90 text-white rounded-full py-6">
                <Share2 className="w-4 h-4 mr-2" /> {sharing ? "Creating card…" : "Share my recap"}
              </Button>
              <Button data-testid="recap-close" onClick={onClose} variant="outline"
                className="flex-1 rounded-full py-6 border-copper/40 text-copper hover:bg-copper/10">
                Done 💛
              </Button>
            </div>

            <ShareCard cardRef={cardRef} data={data} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ShareCard({ cardRef, data }) {
  const r = data?.received, g = data?.giving;
  const first = data?.name?.split(" ")[0] || "A member";
  return (
    <div ref={cardRef} style={{
      position: "fixed", left: "-9999px", top: 0, width: "440px", height: "440px",
      background: "linear-gradient(135deg, #FBD2E0 0%, #E7D8F7 45%, #BEE7CC 100%)",
      fontFamily: "'DM Sans', sans-serif", padding: "34px", boxSizing: "border-box",
      display: "flex", flexDirection: "column", color: "#5C3B50",
    }}>
      <div style={{ fontSize: "13px", letterSpacing: "3px", textTransform: "uppercase", fontWeight: 700, color: "#C05A82" }}>HAPLAP · My Week 💛</div>
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: "30px", fontWeight: 700, marginTop: "6px", lineHeight: 1.1 }}>
        {first} made kindness happen
      </div>
      <div style={{ display: "flex", gap: "12px", marginTop: "22px" }}>
        <Tile label="supporters" value={r?.new_supporters ?? 0} />
        <Tile label="received" value={`$${(r?.total ?? 0).toFixed(0)}`} />
        <Tile label={g?.people_helped ? "people helped" : "given"} value={g?.people_helped ? g.people_helped : `$${(g?.total ?? 0).toFixed(0)}`} />
      </div>
      <div style={{ marginTop: "auto", fontSize: "16px", fontWeight: 600, lineHeight: 1.4 }}>
        Every penny went straight to real people — not overhead, not CEOs. Just neighbours helping neighbours. ✨
      </div>
      <div style={{ marginTop: "16px", fontSize: "13px", color: "#8A6A7C", fontWeight: 600 }}>
        have a penny · leave a penny · need a penny · take a penny
      </div>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div style={{ flex: 1, background: "rgba(255,255,255,0.72)", borderRadius: "18px", padding: "14px 8px", textAlign: "center" }}>
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: "30px", fontWeight: 700, color: "#5C3B50" }}>{value}</div>
      <div style={{ fontSize: "11px", color: "#8A6A7C" }}>{label}</div>
    </div>
  );
}
